import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { huntingApi } from '../services/api';
import StarRating from '../components/StarRating';

interface MonthDayScore {
  date: string;
  dayOfMonth: number;
  dayOfWeek: number;
  amRating: number;
  pmRating: number;
  amWeightedScore: number;
  pmWeightedScore: number;
  lunarPhase: string;
  hasWeatherData: boolean;
  isPast: boolean;
}

interface MonthlyOutlook {
  zipcode: string;
  year: number;
  month: number;
  monthName: string;
  days: MonthDayScore[];
}

function getMoonIcon(phase: string): string {
  const icons: { [key: string]: string } = {
    'New Moon': '🌑',
    'Waxing Crescent': '🌒',
    'First Quarter': '🌓',
    'Waxing Gibbous': '🌔',
    'Full Moon': '🌕',
    'Waning Gibbous': '🌖',
    'Last Quarter': '🌗',
    'Waning Crescent': '🌘',
  };
  return icons[phase] || '🌙';
}

function getRatingClass(rating: number): string {
  if (rating >= 5) return 'score-excellent';
  if (rating >= 4) return 'score-good';
  if (rating >= 3) return 'score-average';
  if (rating >= 2) return 'score-below';
  return 'score-poor';
}

function getDayHighlightClass(amRating: number, pmRating: number): string {
  if (amRating >= 5 && pmRating >= 5) return 'cal-day-highlight-dark-green';
  if ((amRating >= 5 && pmRating >= 4) || (amRating >= 4 && pmRating >= 5)) return 'cal-day-highlight-light-green';
  if (amRating >= 4 && pmRating >= 4) return 'cal-day-highlight-yellow';
  return '';
}

function MonthlyViewPage() {
  const { user, activeLocation } = useAuth();
  const zipcode = activeLocation?.zipcode;
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<MonthlyOutlook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locationName, setLocationName] = useState('');
  const locationLabel = [activeLocation?.name, locationName].filter(Boolean).join(' · ');

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const canGoBack = year > currentYear || (year === currentYear && month > currentMonth);

  useEffect(() => {
    if (zipcode) {
      fetchMonthly(zipcode, year, month);
      huntingApi.getLocation(zipcode).then(res => {
        if (res.data.city && res.data.stateAbbr) {
          setLocationName(`${res.data.city}, ${res.data.stateAbbr}`);
        }
      }).catch(() => {});
    }
  }, [zipcode, year, month, user?.tier]);

  const fetchMonthly = async (zipcode: string, y: number, m: number) => {
    setLoading(true);
    setError('');
    try {
      const response = await huntingApi.getMonthly(zipcode, y, m);
      setData(response.data);
    } catch (err: any) {
      setError('Failed to load monthly data. Please try again.');
      console.error('Monthly fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (!canGoBack) return;
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  };

  const goForward = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  };

  if (loading) {
    return (
      <div className="monthly-view">
        <div className="loading" role="status">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading monthly calendar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="monthly-view">
        <div className="error-state">
          <p>{error}</p>
          <button
            onClick={() => zipcode && fetchMonthly(zipcode, year, month)}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Build calendar grid
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  const dayMap = new Map(data.days.map(d => [d.dayOfMonth, d]));

  const weeks: (MonthDayScore | null)[][] = [];
  let currentWeek: (MonthDayScore | null)[] = [];

  // Fill leading empty cells
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dayData = dayMap.get(day) || null;
    currentWeek.push(dayData);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Fill trailing empty cells
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return (
    <div className="monthly-view">
      <div className="monthly-card">
        <div className="monthly-header">
          <button
            className="day-nav-btn"
            onClick={goBack}
            disabled={!canGoBack}
            aria-label="Previous month"
          >
            ◀
          </button>
          <div className="monthly-title">
            <h2>{data.monthName} {data.year}</h2>
            {locationLabel && <span className="monthly-location">{locationLabel}</span>}
          </div>
          <button
            className="day-nav-btn"
            onClick={goForward}
            aria-label="Next month"
          >
            ▶
          </button>
        </div>

        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="calendar-weekday">{d}</div>
            ))}
          </div>

          {weeks.map((week, wi) => (
            <div key={wi} className="calendar-week">
              {week.map((day, di) => (
                <div
                  key={di}
                  className={`calendar-day ${day ? 'calendar-day-active' : 'calendar-day-empty'} ${day ? 'calendar-day-clickable' : ''} ${day && !day.isPast && !day.hasWeatherData ? 'calendar-day-no-weather' : ''} ${day && !day.isPast ? getDayHighlightClass(day.amRating, day.pmRating) : ''} ${day && day.isPast ? 'calendar-day-past' : ''}`}
                  onClick={day ? () => navigate(`/daily?date=${day.date}`) : undefined}
                  onKeyDown={day ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/daily?date=${day.date}`); } } : undefined}
                  role={day ? 'button' : undefined}
                  tabIndex={day ? 0 : undefined}
                  aria-label={day ? `${data?.monthName} ${day.dayOfMonth}${day.isPast ? '' : `, morning ${day.amRating} of 5 stars, evening ${day.pmRating} of 5 stars${day.hasWeatherData ? '' : ', moon only'}`}, ${day.lunarPhase}. View daily forecast` : undefined}
                >
                  {day && (
                    <>
                      <div className="cal-day-header">
                        <span className="cal-day-number">{day.dayOfMonth}</span>
                        <span className="cal-moon-icon" title={day.lunarPhase}>{getMoonIcon(day.lunarPhase)}</span>
                      </div>
                      {!day.isPast && (
                        <>
                          <div className="cal-scores">
                            <div className={`cal-score-row ${getRatingClass(day.amRating)}`}>
                              <span className="cal-period-label">AM</span>
                              <StarRating rating={day.amRating} size="small" />
                              <span className="cal-rating-compact">{day.amRating}/5</span>
                            </div>
                            <div className={`cal-score-row ${getRatingClass(day.pmRating)}`}>
                              <span className="cal-period-label">PM</span>
                              <StarRating rating={day.pmRating} size="small" />
                              <span className="cal-rating-compact">{day.pmRating}/5</span>
                            </div>
                          </div>
                          <span className="cal-data-source-icon" title={day.hasWeatherData ? 'Weather data available' : 'Moon only'}>
                            {day.hasWeatherData ? '⛅' : '🌙'}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="monthly-legend">
          <span className="monthly-legend-item">
            <span className="legend-icon">⛅</span> Weather data available
          </span>
          <span className="monthly-legend-item">
            <span className="legend-icon">🌙</span> Moon only
          </span>
        </div>
      </div>
    </div>
  );
}

export default MonthlyViewPage;
